import React, { useEffect, useState, useCallback } from 'react';
import { Filter } from 'lucide-react';
import { useTitle } from '../../../context/TitleContext';
import { useToast } from '../../../context/ToastContext';
import { SearchInput } from '../SearchInput';
import { ExportButton } from '../ExportButton';
import { OrganizationsTable } from '../OrganizationsTable';
import { SubscriptionBadge } from '../SubscriptionBadge';
import { 
  getOrganizations, 
  exportOrganizationsToCSV, 
  downloadCSV 
} from '../../../lib/adminQueries';
import { Organization, OrganizationFilters } from '../../../models/admin';
import { SubscriptionStatus } from '../../../lib/supabase';

export const OrganizationsList: React.FC = () => {
  const { setTitle } = useTitle();
  const { showToast } = useToast();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<OrganizationFilters>({
    page: 1,
    limit: 20,
    sortBy: 'created_at',
    sortOrder: 'desc',
  });
  const [totalCount, setTotalCount] = useState(0);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    setTitle('Organizations');
    document.title = 'Organizations';
  }, [setTitle]);

  const fetchOrganizations = useCallback(async () => {
    setLoading(true);
    try {
      const { data, count } = await getOrganizations(filters);
      setOrganizations(data);
      setTotalCount(count);
    } catch (error) {
      console.error('Error fetching organizations:', error);
      showToast('Failed to load organizations', 'error');
    } finally {
      setLoading(false);
    }
  }, [filters, showToast]);

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  const handleSearch = useCallback((query: string) => {
    setFilters(prev => ({ ...prev, search: query, page: 1 }));
  }, []);

  const handleStatusFilter = (status: SubscriptionStatus | undefined) => {
    setFilters(prev => ({ ...prev, status, page: 1 }));
  };

  const handleSort = (column: string, direction: 'asc' | 'desc') => {
    setFilters(prev => ({ 
      ...prev, 
      sortBy: column as OrganizationFilters['sortBy'], 
      sortOrder: direction,
      page: 1,
    }));
  };

  const handleExport = () => {
    try {
      const csv = exportOrganizationsToCSV(organizations);
      downloadCSV(csv, `organizations-${new Date().toISOString().split('T')[0]}.csv`);
      showToast('Organizations exported successfully', 'success');
    } catch (error) {
      console.error('Error exporting:', error);
      showToast('Failed to export organizations', 'error');
    }
  };

  const handlePageChange = (newPage: number) => {
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  const totalPages = Math.ceil(totalCount / (filters.limit || 20));

  const statusFilters: Array<{ label: string; value: SubscriptionStatus | undefined }> = [
    { label: 'All', value: undefined },
    { label: 'Trial', value: 'trial' },
    { label: 'Active', value: 'active' },
    { label: 'Canceled', value: 'canceled' },
    { label: 'Past Due', value: 'past_due' },
    { label: 'Expired', value: 'expired' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Organizations</h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Manage and view all organizations ({totalCount})
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <Filter className="h-4 w-4 mr-2" />
            Filters
          </button>
          <ExportButton onExport={handleExport} />
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 space-y-4">
        <SearchInput
          placeholder="Search organizations..."
          onSearch={handleSearch}
          className="w-full md:w-96"
        />

        {showFilters && (
          <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex flex-wrap gap-2">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300 self-center mr-2">
                Status:
              </span>
              {statusFilters.map((filter) => (
                <button
                  key={filter.label}
                  onClick={() => handleStatusFilter(filter.value)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    filters.status === filter.value
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {filter.label}
                  {filter.value && (
                    <span className="ml-1">
                      <SubscriptionBadge status={filter.value} size="sm" />
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Organizations Table */}
      <OrganizationsTable
        organizations={organizations}
        loading={loading}
        onSort={handleSort}
      />

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between bg-white dark:bg-gray-800 rounded-lg shadow px-4 py-3">
          <div className="text-sm text-gray-700 dark:text-gray-300">
            Page {filters.page} of {totalPages} ({totalCount} total)
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => handlePageChange(filters.page! - 1)}
              disabled={filters.page === 1 || loading}
              className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => handlePageChange(filters.page! + 1)}
              disabled={filters.page === totalPages || loading}
              className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
